import React, { useCallback } from 'react';
import { ToggleButtonGroup } from '@mui/material';
import clsx from 'clsx';
import { IOption, Nullable, ToggleButton } from '@linetweet/linetweet-ui';

import { SlotButtonGroupProps, SlotButtonGroupType } from '../types';

import styles from './SlotButtonGroup.module.scss';

function SlotButtonGroupComponent<TValue extends IOption>({
  value,
  onChange,
  options,
  before,
  isDisabled,
  dataTestId,
}: SlotButtonGroupProps<TValue>) {
  // wrapper to reset ToggleButtonGroup inner children logic.
  // The wrapping div carries a z-index so it stays interactive when an
  // ancestor `Spin` overlays the slot buttons during loading.
  // eslint-disable-next-line react/require-default-props
  const Before = useCallback(({ before: beforeElement }: { before?: React.ReactNode }) => {
    if (!beforeElement) return null;

    return (
      <div className={styles.before}>
        {beforeElement}
      </div>
    );
  }, []);

  return (
    <ToggleButtonGroup
      data-testid={dataTestId}
      className={clsx(styles.root, { [styles.singleRow]: options.length <= 3 })}
      exclusive
      value={value}
      onChange={(event, updatedValue) => {
        const currentValue = value ? value.value : null;

        let nextValue: Nullable<TValue> = null;
        if (updatedValue !== null && updatedValue.value !== currentValue) {
          nextValue = updatedValue;
        }

        onChange(nextValue);
      }}
    >
      <Before before={before} />

      {options.map((slotItem) => (
        <ToggleButton
          key={`slots-picker-slot-${slotItem.value}`}
          variant="rounded"
          color="secondary"
          className={styles.button}
          disabled={!!isDisabled}
          selected={slotItem.value === value?.value}
          value={slotItem}
          data-testid={`slot-${slotItem.value}`}
        >
          {slotItem.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

SlotButtonGroupComponent.defaultValues = {
  isDisabled: undefined,
};

export const SlotButtonGroup = React.memo(SlotButtonGroupComponent) as SlotButtonGroupType;
